// src/pages/Calendar.js
import React, { useMemo, useState, useEffect } from "react";
import MonthGrid from "../components/calender/MonthGrid";
import ScheduleDetail from "../components/calender/ScheduleDetail";
import AddScheduleModal from "../components/calender/AddScheduleModal";
import MonthPicker from "../components/calender/MonthPicker";
import { fetchCalendarMonth, createSchedule, updateSchedule } from "../services/calendar.service";


const TYPE_LABELS = {
  MEETING: "Meetings",
  APPOINTMENT: "Appointments",
  PRESENTATION: "Presentations",
  EVENT: "Events",
};

const TYPE_CLASS = {
  MEETING: "meeting",
  APPOINTMENT: "appointment",
  PRESENTATION: "presentation",
  EVENT: "event",
};


const pad = (n) => String(n).padStart(2, "0");
const ymdLocal = (iso) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export default function CalendarPage() {
  const [calendarData, setCalendarData] = useState({ colors: {}, events: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const colors = calendarData.colors || {};

  const [items, setItems] = useState([])

  const [view, setView] = useState("month");
  const [hiddenTypes, setHiddenTypes] = useState(new Set());
  const [monthDate, setMonthDate] = useState(new Date());

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDayEvents, setSelectedDayEvents] = useState([]); // all schedules on that day

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null => create, object => edit

  // Fetch calendar data on mount and when month changes
  useEffect(() => {
    const loadCalendarData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchCalendarMonth({
          month: monthDate.getMonth() + 1, // JS months are 0-based
          year: monthDate.getFullYear(),
        });
        setCalendarData(data);
        const mappedItems = data.events.map(e => ({
          importance: e.importance ?? "normal",
          ...e,
        }));
        setItems(mappedItems);

        // Sort events: most recent (by createdAt) first, then existing priority
        const sortedEvents = [...mappedItems].sort((a, b) => {
          // First, sort by createdAt descending (most recent first)
          if (a.createdAt && b.createdAt) {
            return new Date(b.createdAt) - new Date(a.createdAt);
          }
          // If no createdAt, fall back to existing sorting
          const aIsMeetingOrEvent = a.type === 'MEETING' || a.type === 'EVENT';
          const bIsMeetingOrEvent = b.type === 'MEETING' || b.type === 'EVENT';
          if (aIsMeetingOrEvent && !bIsMeetingOrEvent) return -1;
          if (!aIsMeetingOrEvent && bIsMeetingOrEvent) return 1;
          if (a.importance === "important" && b.importance !== "important") return -1;
          if (b.importance === "important" && a.importance !== "important") return 1;
          return new Date(a.start) - new Date(b.start);
        });

        setSelectedDayEvents(sortedEvents);
      } catch (err) {
        console.error("Failed to fetch calendar data:", err);
        setError(err.message);
        // Fallback to empty data
        setCalendarData({ colors: {}, events: [] });
        setItems([]);
        setSelectedDayEvents([]);
      } finally {
        setLoading(false);
      }
    };

    loadCalendarData();
  }, [monthDate]);

  const countsByType = useMemo(() => {
    const m = { MEETING: 0, APPOINTMENT: 0, PRESENTATION: 0, EVENT: 0 };
    for (const e of items) if (m[e.type] != null) m[e.type] += 1;
    return m;
  }, [items]);

  function toggleType(t) {
    setHiddenTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  }

  function openCreate() {
    setEditing(null);
    setModalOpen(true);
  }

  function openEdit(ev) {
    setEditing(ev);
    setModalOpen(true);
  }


  function handleEventClick(ev) {
    setSelectedEvent(ev);
    const key = ymdLocal(ev.start);
    const dayList = items
      .filter((x) => ymdLocal(x.start) === key)
      .sort((a, b) => new Date(a.start) - new Date(b.start));
    setSelectedDayEvents(dayList);
  }


  async function handleCreate(newEvent) {
    try {
      const createdEvent = await createSchedule(newEvent);
      setItems((prev) => {
        const next = [...prev, createdEvent];
        setSelectedEvent(createdEvent);
        const key = ymdLocal(createdEvent.start);
        setSelectedDayEvents(
          next
            .filter((x) => ymdLocal(x.start) === key)
            .sort((a, b) => new Date(a.start) - new Date(b.start))
        );
        return next;
      });
      alert("Schedule created successfully!");
      window.location.reload();
    } catch (error) {
      console.error("Failed to create schedule:", error);
      alert("Failed to create schedule. Please try again.");
    }
  }

  async function handleUpdate(updated) {
    try {
      const updatedEvent = await updateSchedule(updated.id, updated);
      setItems((prev) => {
        const next = prev.map((e) => (e.id === updatedEvent.id ? updatedEvent : e));
        setSelectedEvent(updatedEvent);
        const key = ymdLocal(updatedEvent.start);
        setSelectedDayEvents(
          next
            .filter((x) => ymdLocal(x.start) === key)
            .sort((a, b) => new Date(a.start) - new Date(b.start))
        );
        return next;
      });
      alert("Schedule updated successfully!");
      window.location.reload();
    } catch (error) {
      console.error("Failed to update schedule:", error);
      alert("Failed to update schedule. Please try again.");
    }
  }

 
  const defaultDateForCreate = useMemo(
    () => {
      if (selectedEvent) {
        const date = new Date(selectedEvent.start);
        return date instanceof Date && !isNaN(date) ? date : new Date();
      }
      return new Date();
    },
    [selectedEvent]
  );

  if (loading) {
    return (
      <div className="page page--calendar">
        <div className="loading">Loading calendar...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page page--calendar">
        <div className="error">Error loading calendar: {error}</div>
      </div>
    );
  }

  return (
    <div className="page page--calendar">
      <div className="cal-month-changer">
        <MonthPicker value={monthDate} onChange={setMonthDate}
        view={view} setView ={setView} openCreate={openCreate}/>
      </div>
      <div className="cal-toolbar card">
        <div className="cal-type-chips">
          {Object.keys(TYPE_LABELS).map((t) => (
            <button
              key={t}
              className={`chip ${hiddenTypes.has(t) ? "is-muted" : ""}`}
              style={{ borderColor: colors[t] }}
              onClick={() => toggleType(t)}
            >
             
              <span className={`order-dot order-dot--${TYPE_CLASS[t]}`} aria-hidden="true" />
              {TYPE_LABELS[t]} <span className="muted">({countsByType[t] || 0})</span>
            </button>
          ))}
        </div>   
      </div>

      <div className="cal-layout">
        <div className="cal-main">
          <MonthGrid
            monthDate={monthDate}
            events={items}
            colors={colors}
            hiddenTypes={hiddenTypes}
            onEventClick={handleEventClick}
          />
        </div>

        <ScheduleDetail
          eventsForDay={selectedDayEvents}
          onClose={() => {
            setSelectedEvent(null);
            setSelectedDayEvents([]);
          }}
          onEdit={openEdit}
        />
      </div>

      <AddScheduleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={editing ? "edit" : "create"}
        initialEvent={editing}
        defaultDate={defaultDateForCreate}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        colors={colors}
      />
    </div>
  );
}
